import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { classes } from "./tokens";

/**
 * FAQ section — 9 questions written for "People Also Ask" rich-snippet
 * eligibility. The same array is emitted as FAQPage JSON-LD in CarService.jsx.
 */
export const faqItems = [
  {
    q: "What's included in Wisedrive general car service?",
    a: "Our general service includes engine oil replacement (OEM grade), oil filter replacement, air filter replacement, wiper fluid top-up, coolant top-up (200 ml), brake fluid top-up, heater / spark plugs checking, fuel filter checking, battery water top-up, front brake pads servicing, and a full car OBD-II scan. Doorstep service is included across all 500+ serviceable cities — service in about 1 hour at your doorstep.",
  },
  {
    q: "Do I have to pay in advance to book a service?",
    a: "No advance is required for booking. Simply share your car details and mobile number to get a callback. Our team will confirm pickup/service timing and pricing — you pay via UPI, card, or cash only after the service is completed at your doorstep and you're fully satisfied.",
  },
  {
    q: "Is doorstep service really free?",
    a: "Yes — included free with every general service. We handle pickup and drop within our standard service zones (100+ pincodes across Bangalore, Delhi-NCR, Mumbai, Hyderabad, Chennai, Pune, and more being added every week). Your certified driver is GPS-tracked and the vehicle is fully insured during transit.",
  },
  {
    q: "How long does a general service take?",
    a: "A typical general service takes 90–120 minutes at our workshop. End-to-end (pickup → service → delivery) is usually completed the same day — book by 11 AM for same-day return, or by 4 PM for next-morning return.",
  },
  {
    q: "Are OEM parts really used, or after-market?",
    a: "We use OEM-grade parts as standard. For every oil/filter/fluid used, we share the actual box/bottle photo via WhatsApp before installation — full transparency, zero fake parts. For premium OEM-branded specifics (e.g., genuine Maruti Suzuki dealership parts), there's a small add-on of ₹300–₹500 which is disclosed before we start.",
  },
  {
    q: "What if my car needs something beyond the general service?",
    a: "If our 25-point inspection surfaces anything beyond general service (e.g., brake pad replacement, battery dead, clutch issue), you get a detailed quote with photo evidence. Nothing is done without your explicit approval — and you only pay for what's done.",
  },
  {
    q: "What's the warranty on a Wisedrive service?",
    a: "Every general service is covered by our 1,000 km / 30-day warranty — whichever comes first. If anything we serviced or replaced has an issue, we fix it free of charge. For specific parts (battery, clutch, etc.), manufacturer warranty passes through as-is.",
  },
  {
    q: "Which cars do you service?",
    a: "All major Indian passenger cars and most premium brands: Maruti Suzuki, Hyundai, Tata, Mahindra, Kia, Toyota, Honda, Nissan, Renault, Skoda, Volkswagen, MG, Jeep, and more. Enter your registration number or pick make/model above to confirm yours is supported and see the exact price.",
  },
  {
    q: "Can I reschedule or cancel after booking?",
    a: "Yes. You can reschedule or cancel any time before our driver arrives for pickup, via WhatsApp or the phone number in your booking confirmation. Since there's no advance payment, cancellation is free and instant.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      className="py-20 lg:py-28 bg-white border-t border-slate-200"
      data-testid="faq-section"
    >
      <div className={classes.container}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className={classes.overline}>Frequently Asked</span>
            <h2 className={`${classes.h2} mt-3`}>
              Answers, not<br />
              <span className="text-slate-500">fine print.</span>
            </h2>
            <p className="mt-5 text-slate-600 max-w-sm">
              Still on the fence? WhatsApp us — a real human replies in under 3 minutes.
            </p>
          </div>

          <div className="lg:col-span-8 border-t border-slate-200">
            {faqItems.map((item, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className="border-b border-slate-200">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-start gap-4 py-6 text-left group"
                    data-testid={`faq-q-${i}`}
                  >
                    <span className="font-mono text-xs font-bold text-blue-600 mt-1 flex-shrink-0 w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 text-base sm:text-lg font-bold text-slate-900 group-hover:text-blue-700 transition-colors pr-4">
                      {item.q}
                    </span>
                    <span
                      className={`flex-shrink-0 w-8 h-8 rounded-full border border-slate-300 inline-flex items-center justify-center transition-all ${
                        isOpen ? "bg-slate-900 border-slate-900 text-white rotate-45" : "text-slate-600"
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-12 pr-4 pb-6 text-slate-600 leading-relaxed">
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
